import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import NewComment from './components/NewComment/NewComment';
import PublishedComment from './components/PublishedComment/PublishedComment';
import styles from './CommentPopup.scss';

const CommentPopup = ({
  comments,
  onClose,
  onComment,
  onCancel,
  commentData,
  commentIdToUpdate,
  actions,
  mentionsAllowed,
  mentionableUsersArray,
  questionId
}) => {
  const commentsRef = useRef(null);
  const prevComment = useRef(null);
  const [commentButtonText, setCommentButtonText] = useState('');
  const [commentToUpdate, setCommentToUpdate] = useState('');
  const [updatedComment, setUpdatedComment] = useState('');
  const [wasCommentCancelled, setWasCommentCancelled] = useState(false);

  const scrollToBottom = () => {
    if(!isEmpty(comments)) {
      commentsRef.current.scrollTop = commentsRef.current.scrollHeight;
    }
  };

  const setButtonText = () => {
      let buttonText = commentData.commentButtonText;
      if (!isEmpty(comments)) {
          if (commentIdToUpdate) {
            buttonText = commentData.updateButtonText;
          } else {
            const lastCommentAuthor = get(comments[comments.length - 1], 'author.name', '');
            buttonText = lastCommentAuthor !== commentData.user ?
                commentData.replyButtonText :
                commentData.commentButtonText;
          }
      }
      setCommentButtonText(buttonText);
  }

  const getCommentContentToUpdate = () => {
    if (commentIdToUpdate) {
        const foundComment = comments.find(comment => comment.id === commentIdToUpdate);
        if (foundComment) {
            setCommentToUpdate(foundComment.content);
        }
    }
  };

  const confirmUpdatedComment = (commentId) => {
    setUpdatedComment(commentId);
    setTimeout(() => {
        setUpdatedComment(null);
    }, 500)
  };

  const cancelComment = () => {
      setWasCommentCancelled(true);
      onCancel();
  };

  useEffect(() => {
    setButtonText();
    getCommentContentToUpdate();
    if (!wasCommentCancelled && prevComment.current !== '' && !commentIdToUpdate) {
        confirmUpdatedComment(prevComment.current);
    }
    prevComment.current = commentIdToUpdate;
  }, [commentIdToUpdate]);

  useEffect(() => {
    setButtonText();
    scrollToBottom();
  }, [comments.length]);

  return (
    <div
        className={styles.commentPopup}
        data-testid={`comments-popOver__question-${questionId}`}
    >
      { comments.length > 0 &&
        <div
            className={styles.comments}
            ref={commentsRef}
            data-testid='comments-popOver__comments-list'
        >
          {comments.map((comment, id) =>
            <PublishedComment
                comment={comment}
                key={id}
                isAdminText={commentData.isAdminText}
                actions={actions}
                wasUpdated={comment.id === updatedComment}
            />,
          )}
        </div>
      }
      <NewComment
        commentContent={commentToUpdate}
        onComment={onComment}
        onCancel={cancelComment}
        onClose={onClose}
        placeholder={commentData.placeholder}
        cancelButtonText={commentData.cancelButtonText}
        commentButtonText={commentButtonText}
        user={commentData.user}
        noComments={isEmpty(comments)}
        mentionsAllowed={mentionsAllowed}
        mentionableUsersArray={mentionableUsersArray}
      />
    </div>
  )
}

CommentPopup.propTypes = {
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      author: PropTypes.shape({
        email: PropTypes.string,
        name: PropTypes.string,
      }),
      createdAt: PropTypes.string,
      content: PropTypes.string,
      userAdmin: PropTypes.bool,
      allowedActions: PropTypes.arrayOf(PropTypes.string)
    }),
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onComment: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  commentData: PropTypes.shape({
    placeholder: PropTypes.string.isRequired,
    cancelButtonText: PropTypes.string.isRequired,
    commentButtonText: PropTypes.string.isRequired,
    replyButtonText: PropTypes.string.isRequired,
    updateButtonText: PropTypes.string.isRequired,
    isAdminText: PropTypes.string,
    user: PropTypes.string,
  }).isRequired,
  actions: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string,
    label: PropTypes.string,
    icon: PropTypes.string,
    callback: PropTypes.func
  })),
  commentIdToUpdate: PropTypes.string,
  mentionsAllowed: PropTypes.bool,
  mentionableUsersArray: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    display: PropTypes.string
  })),
  questionId: PropTypes.string,
};

CommentPopup.defaultProps = {
    actions: [],
    commentIdToUpdate: '',
    mentionsAllowed: false,
    mentionableUsersArray: [],
    questionId: ''
};

export default CommentPopup;
